/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/**
 * AnticipateHorizonScanV2 — the new Horizon Scan dashboard (gated by tenant.modules.horizonV2).
 * Renders the stored theme model: fixed L2 THEMES → up-to-3 dynamic SUB-THEMES → news-stream incidents,
 * led by a "Top Emerging Themes" band. Reads GET /anticipateHorizonScan/v2/model (precomputed); a
 * super-admin "Rebuild model" button triggers the background regeneration.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const API = protectedResources.apiTodoList.endpoint;
// Palette pulled from the app's Material-Dashboard theme (assets/theme/base/colors.js) so the
// dashboard matches the rest of the product, not a bespoke set: accent = info.main (brand purple),
// ink = dark.main, muted = text.focus, bg = background.default, lines = grey.300/400, hot = warning.main.
const C = {
  bg: "#f0f2f5",
  surface: "#ffffff",
  ink: "#344767",
  muted: "#7b809a",
  line: "#dee2e6",
  lineStrong: "#ced4da",
  accent: "#52009b",
  accentSoft: "#ede7f6",
  hot: "#fb8c00",
  hotSoft: "#fff3e0",
};

const nf = (n) => Number(n || 0).toLocaleString();
const clean = (s) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim();
const pretty = (t) =>
  String(t || "")
    .replace(/-/g, " ")
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase())
    .replace(/\b(Ai|It|Saf|Atc|Eu|Us|Uk|Mro|Ndc|Ceo|Esg)\b/gi, (s) => s.toUpperCase());

// Group a theme-detail's incidents under their sub-themes (same grouping the Dashboard card shows).
// Returns [{ name, meta, incidents }]. When drilled into a single sub-theme, or a theme has ≤1
// sub-theme, returns one unlabelled group so the caller renders a flat list.
function groupIncidentsBySubTheme(detail) {
  const incidents = detail?.incidents || [];
  const subThemes = detail?.subThemes || [];
  if (detail?.scoped || subThemes.length <= 1) return [{ name: "", meta: null, incidents }];
  const order = subThemes.map((s) => s.name);
  const byName = new Map(order.map((n) => [n, []]));
  const other = [];
  incidents.forEach((inc) => {
    if (inc.subTheme && byName.has(inc.subTheme)) byName.get(inc.subTheme).push(inc);
    else other.push(inc);
  });
  const groups = order
    .map((n) => ({
      name: n,
      meta: subThemes.find((s) => s.name === n) || null,
      incidents: byName.get(n),
    }))
    .filter((g) => g.incidents.length);
  if (other.length) groups.push({ name: "More developments", meta: null, incidents: other });
  return groups;
}

// ID188 — the priority pop-up: a heading ("Priority N Theme") separated from the final assessment —
// consequence (level + type, e.g. "Level 3 Airworthiness") and the likelihood band. No base/multiplier.
function priorityPopup(p) {
  const cons = [p.consequenceLevelLabel, p.consequenceTypeLabel].filter(Boolean).join(" ");
  return (
    <span style={{ display: "block", fontSize: "0.72rem", lineHeight: 1.55, minWidth: 150 }}>
      <span
        style={{
          display: "block",
          fontWeight: 800,
          fontSize: "0.78rem",
          paddingBottom: 4,
          marginBottom: 5,
          borderBottom: "1px solid rgba(255,255,255,0.25)",
        }}
      >
        Priority {p.priorityBand} Theme
      </span>
      <span style={{ display: "block" }}>
        <b>Consequence:</b> {cons || "—"}
      </span>
      <span style={{ display: "block" }}>
        <b>Likelihood:</b> {p.plausibilityLabel || "—"}
      </span>
    </span>
  );
}

// `dormant` = the item carries a stored priority band but has no recent alerts, so it does NOT
// drive its category's live band. Render it outlined/greyed (not solid) so a stale P1 chip no
// longer reads as a live P1 next to a quieter category badge. The band label is kept — it is still
// the stored rating — just visually de-emphasised, with the reason surfaced in the tooltip.
function PriorityBadge({ p, dormant = false }) {
  if (!p || !p.priorityBand) return null;
  const color = p.matrixRatingColor || "#5B6B78";
  const badge = (
    <span
      style={{
        fontSize: "0.64rem",
        fontWeight: 750,
        color: dormant ? C.muted : "#fff",
        background: dormant ? "transparent" : color,
        border: dormant ? `1px solid ${C.line}` : "none",
        opacity: dormant ? 0.75 : 1,
        borderRadius: 6,
        padding: dormant ? "0 5px" : "1px 6px",
        fontVariantNumeric: "tabular-nums",
        cursor: "help",
      }}
    >
      P{p.priorityBand}
    </span>
  );
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        dormant
          ? "No recent alerts — dormant, so it does not raise the category priority."
          : priorityPopup(p)
      }
    >
      {badge}
    </Tooltip>
  );
}

// Top emerging sub-themes for a theme's dashboard box: real sub-themes first (by priority band then
// recent activity), the catch-all "More developments / Other" ranked last, capped at 3.
function topSubThemes(theme) {
  const subs = Array.isArray(theme?.subThemes) ? theme.subThemes.slice() : [];
  return subs
    .sort((a, b) => {
      if (!!a.isOther !== !!b.isOther) return a.isOther ? 1 : -1;
      return (
        (a.prioritisation?.priorityBand || 99) - (b.prioritisation?.priorityBand || 99) ||
        (b.recentAlerts || 0) - (a.recentAlerts || 0) ||
        (b.alertCount || 0) - (a.alertCount || 0)
      );
    })
    .slice(0, 3);
}

const EYEBROW = {
  color: C.muted,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontSize: "0.58rem",
  display: "block",
};

// Dashboard box for ONE L2 "Risk Category": header (category name + priority + counts + read/unread)
// separated from its top-3 emerging sub-themes. Clicking anywhere opens the theme detail. (ID186)
function CategoryBox({ theme, unread, onOpen }) {
  const subs = topSubThemes(theme);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen();
      }}
      title={`View news for ${clean(theme.theme)}`}
      sx={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: "14px",
        p: 1.5,
        cursor: "pointer",
        transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": { borderColor: C.accent, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" },
      }}
    >
      {/* Risk Category header */}
      <MDTypography variant="caption" sx={EYEBROW}>
        Risk Category
      </MDTypography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
        <Icon
          sx={{ fontSize: "16px !important", color: unread ? C.accent : C.muted, flexShrink: 0 }}
          title={unread ? "Unread — new or changed" : "Read"}
        >
          {unread ? "markunread" : "drafts"}
        </Icon>
        <MDTypography
          variant="h6"
          sx={{
            fontWeight: unread ? 800 : 700,
            fontSize: "0.9rem",
            color: C.ink,
            lineHeight: 1.2,
            flex: 1,
          }}
        >
          {clean(theme.theme)}
        </MDTypography>
        <PriorityBadge p={theme.prioritisation} />
      </Box>
      <MDTypography
        variant="caption"
        sx={{ color: C.muted, fontSize: "0.66rem", mt: 0.35, display: "block" }}
      >
        {theme.noNews ? (
          <span style={{ fontStyle: "italic" }}>on the radar — no news yet</span>
        ) : (
          <>
            <b style={{ color: C.accent }}>{nf(theme.recentAlerts)}</b> recent ·{" "}
            {nf(theme.alertCount)} total
          </>
        )}
      </MDTypography>

      <Box sx={{ height: "1px", background: C.line, my: 0.9 }} />

      {/* Top Emerging Themes = the L2's top sub-themes (full titles, no truncation) */}
      <MDTypography variant="caption" sx={EYEBROW}>
        Top Emerging Themes
      </MDTypography>
      {subs.length ? (
        <Box sx={{ mt: 0.65, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {subs.map((st, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
              <PriorityBadge p={st.prioritisation} dormant={(st.recentAlerts || 0) === 0} />
              <div style={{ flex: 1 }}>
                <MDTypography
                  variant="body2"
                  sx={{ color: C.ink, fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.25 }}
                >
                  {clean(st.name)}
                </MDTypography>
                {!theme.noNews && (st.recentAlerts || st.alertCount) ? (
                  <MDTypography variant="caption" sx={{ color: C.muted, fontSize: "0.64rem" }}>
                    <b style={{ color: C.accent }}>{nf(st.recentAlerts)}</b> recent ·{" "}
                    {nf(st.alertCount)} total
                  </MDTypography>
                ) : null}
              </div>
            </Box>
          ))}
        </Box>
      ) : (
        <MDTypography
          variant="caption"
          sx={{ color: C.muted, fontStyle: "italic", display: "block", mt: 0.5 }}
        >
          No emerging sub-themes yet.
        </MDTypography>
      )}
    </Box>
  );
}

function AnticipateHorizonScanV2() {
  const { isSuperRiskAdmin } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const navOpenRef = useRef(false); // open a theme passed via navigation state exactly once

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [msg, setMsg] = useState(null);
  const [maxBand, setMaxBand] = useState(1); // priority filter: EXACT band 1|2|3, or "all" — default P1 only
  const userPickedBand = useRef(false); // reserved: once the user picks a filter, stop auto-adjusting
  const [watchlistSel, setWatchlistSel] = useState("all"); // 'all' | 'has' | 'exclude' (watchlist-linked themes)
  const [catSel, setCatSel] = useState([]); // ID185e — filter by risk category (L2 names); [] = all
  const [readMap, setReadMap] = useState({}); // per-viewer read state { themeName: true }
  const [detail, setDetail] = useState(null); // drill-down: { theme, incidents } | null
  const [detailLoading, setDetailLoading] = useState(false);
  // Which sub-theme groups are expanded in the drill-down modal (collapsed by default).
  const [expandedSubs, setExpandedSubs] = useState({});
  const toggleSub = useCallback((name) => setExpandedSubs((p) => ({ ...p, [name]: !p[name] })), []);

  const READ_KEY = "horizonV2_readThemes";
  // Load per-viewer read state once.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(READ_KEY);
      if (raw) setReadMap(JSON.parse(raw) || {});
    } catch {
      /* ignore */
    }
  }, []);
  const persistRead = useCallback((next) => {
    setReadMap(next);
    try {
      window.localStorage.setItem(READ_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);
  // Store the CONTENT VERSION the user last saw for this theme (was a plain `true`). A theme is unread
  // when it's new, or its content-version has since bumped (a material change to what's happening / why
  // it matters / its priority). New articles that don't move those keep the same version → stays read.
  const markRead = useCallback((theme) => {
    const name = theme?.theme;
    if (!name) return;
    const v = theme?.contentVersion || 1;
    setReadMap((prev) => {
      if (prev[name] === v) return prev;
      const next = { ...prev, [name]: v };
      try {
        window.localStorage.setItem(READ_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const isUnread = useCallback(
    (theme) => {
      const seen = readMap[theme?.theme];
      if (seen === true) return false; // legacy read-state (pre content-version) → treat as read
      return seen !== (theme?.contentVersion || 1);
    },
    [readMap]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await executeRef.current("GET", `${API}/anticipateHorizonScan/v2/model`);
      if (res?.success) setDoc(res.data || null);
    } catch (e) {
      /* handled by hook */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rebuild = useCallback(() => {
    setRebuilding(true);
    setMsg(null);
    executeRef
      .current("POST", `${API}/anticipateHorizonScan/v2/rebuild-model`, {})
      .then((res) =>
        setMsg({
          ok: !!res?.success,
          text: res?.message || (res?.success ? "Rebuild started." : "Could not start rebuild."),
        })
      )
      .catch(() => setMsg({ ok: false, text: "Rebuild request failed." }))
      .finally(() => setRebuilding(false));
  }, []);

  // Fetch a fresh short-lived SAS URL for an uploaded source document and open it.
  const openAttachment = useCallback((blobName) => {
    if (!blobName) return;
    executeRef
      .current(
        "GET",
        `${API}/anticipateHorizonScan/v2/attachment?blobName=${encodeURIComponent(blobName)}`
      )
      .then((res) => {
        if (res?.success && res.data?.url) window.open(res.data.url, "_blank", "noopener");
      })
      .catch(() => {});
  }, []);

  // Advance to the Watchlist, pre-populating the risk form. Advancing is done at the SUB-THEME level
  // (PO 2026-09-18): `sub` is the chosen sub-theme ({ name, prioritisation }); its name becomes the risk
  // title and its own prioritisation drives the prefill. The taxonomy anchor stays the L2 theme
  // (Category=L1, Sub-category + Linked theme = the L2), since sub-themes aren't taxonomy nodes. When no
  // sub-theme is passed (a single-story theme with no distinct sub-themes) it advances the L2 itself.
  const advanceToWatchlist = useCallback(
    (d, sub) => {
      if (!d) return;
      const p = (sub && sub.prioritisation) || d.prioritisation || {};
      const title = clean((sub && sub.name) || d.subTheme || d.theme);
      // Derive the time horizon from the plausibility label ("Near Term & Fast" etc.) so the risk
      // form's Time Horizon & Velocity selector pre-selects in timeframe_velocity mode too.
      const plaus = String(p.plausibilityLabel || "").toLowerCase();
      let timeFrame = "";
      if (plaus.includes("near")) timeFrame = "near-term";
      else if (plaus.includes("long")) timeFrame = "long-term";
      else if (plaus.includes("medium")) timeFrame = "medium-term";
      navigate("/anticipate/EmergingRisks", {
        state: {
          prefill: {
            title,
            description: clean(d.summary || d.whyItMatters || ""),
            category: clean(d.l1 || ""),
            subCategory: clean(d.theme || ""),
            linkedHorizonTheme: d.theme || "",
            likelihood: p.plausibilityLabel || "", // used in likelihood mode
            velocity: p.plausibilityLabel || "", // used in timeframe_velocity mode (overallVelocity)
            timeFrame, // pre-selects the time horizon; '' falls back to the form default
            whyItMatters: clean(d.whyItMatters || ""),
          },
        },
      });
    },
    [navigate]
  );

  const openDetail = useCallback((theme, subTheme) => {
    markRead(theme); // opening a theme marks it read at its current content-version (per viewer)
    setDetail({ theme: theme.theme, subTheme: subTheme || "", incidents: null });
    setExpandedSubs({});
    setDetailLoading(true);
    const sub = subTheme ? `&subTheme=${encodeURIComponent(subTheme)}` : "";
    executeRef
      .current(
        "GET",
        `${API}/anticipateHorizonScan/v2/theme-detail?l2=${encodeURIComponent(theme.theme)}${sub}`
      )
      .then((res) => {
        if (res?.success) setDetail(res.data || { theme: theme.theme, incidents: [] });
      })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, []);

  const model = doc?.model || null;

  // Open a theme's detail directly when navigated here with { state: { openThemeL2 } } — e.g. clicking
  // a Priority 1 theme row on the Home page should land on that theme's detail, not the watchlist form.
  useEffect(() => {
    const wanted = location.state?.openThemeL2;
    if (!wanted || navOpenRef.current || loading || !model) return;
    const target = String(wanted).trim().toLowerCase();
    let hit = null;
    (model.groups || []).forEach((g) =>
      (g.themes || []).forEach((t) => {
        if (
          String(t.theme || "")
            .trim()
            .toLowerCase() === target
        )
          hit = t;
      })
    );
    if (hit) {
      navOpenRef.current = true;
      openDetail(hit);
    }
  }, [location.state, loading, model, openDetail]);

  // ID185d (rev): the priority filter defaults to P2 and is cumulative ("and above"), so the default
  // view shows P1 + P2. It stays where the user sets it (no auto-adjust to the most-populated band).

  // ALL L2 themes ranked by priority (P1 first, then recency) — each becomes a Risk Category box.
  const allThemesRanked = useMemo(() => {
    if (!model) return [];
    const flat = [];
    (model.groups || []).forEach((g) =>
      (g.themes || []).forEach((t) => flat.push({ l1: g.l1, t }))
    );
    return flat.sort(
      (a, b) =>
        (a.t.prioritisation?.priorityBand || 99) - (b.t.prioritisation?.priorityBand || 99) ||
        (b.t.recentAlerts || 0) - (a.t.recentAlerts || 0) ||
        (b.t.alertCount || 0) - (a.t.alertCount || 0)
    );
  }, [model]);

  // A theme passes the current filters: within the selected priority band + the watchlist toggle.
  const themePasses = useCallback(
    (t) => {
      if (watchlistSel === "has" && !(t.riskCount > 0)) return false;
      if (watchlistSel === "exclude" && t.riskCount > 0) return false;
      // Exact band (PO 2026-10): P1 shows ONLY P1 categories, P2 only P2, P3 only P3; All shows everything.
      if (maxBand === "all") return true;
      return (t.prioritisation?.priorityBand || 99) === maxBand;
    },
    [maxBand, watchlistSel]
  );

  // ID185e — the risk-category filter options: every L2 theme name in the model.
  const categoryOptions = useMemo(
    () => Array.from(new Set(allThemesRanked.map((x) => clean(x.t.theme)))).sort(),
    [allThemesRanked]
  );

  // The Risk Category boxes shown: pass the priority + watchlist filters AND the category filter.
  const shownThemes = useMemo(
    () =>
      allThemesRanked.filter(
        (x) => themePasses(x.t) && (catSel.length === 0 || catSel.includes(clean(x.t.theme)))
      ),
    [allThemesRanked, themePasses, catSel]
  );

  // Mark every theme currently in the model as read (per viewer).
  const markAllRead = useCallback(() => {
    if (!model) return;
    const next = { ...readMap };
    (model.groups || []).forEach((g) =>
      (g.themes || []).forEach((t) => {
        next[t.theme] = t.contentVersion || 1;
      })
    );
    persistRead(next);
  }, [model, readMap, persistRead]);

  let emptyMessage = "Ask a Super Risk Admin to build the theme model.";
  if (doc?.status === "building")
    emptyMessage = "The model is being generated — reload in a few minutes.";
  else if (isSuperRiskAdmin)
    emptyMessage = 'Click "Rebuild model" to generate it from the current news.';

  return (
    <MDBox sx={{ color: C.ink }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <div>
          <MDTypography
            variant="caption"
            sx={{
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.accent,
              fontWeight: 700,
            }}
          >
            Anticipate · Horizon Scan
          </MDTypography>
          <MDTypography variant="h4" sx={{ letterSpacing: "-0.02em", mt: 0.5 }}>
            Emerging-Risk Themes
          </MDTypography>
        </div>
        {isSuperRiskAdmin && (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={rebuilding}
            onClick={rebuild}
            sx={{ whiteSpace: "nowrap" }}
          >
            {rebuilding ? (
              <CircularProgress size={14} sx={{ mr: 1 }} />
            ) : (
              <Icon sx={{ mr: 0.5 }}>autorenew</Icon>
            )}
            {rebuilding ? "Starting…" : "Rebuild model"}
          </MDButton>
        )}
      </Box>

      {msg && (
        <Box
          sx={{
            mb: 2,
            p: 1.2,
            borderRadius: "8px",
            fontSize: "0.85rem",
            background: msg.ok ? C.accentSoft : "#fdecea",
            color: msg.ok ? C.accent : "#b71c1c",
            border: `1px solid ${msg.ok ? C.accent : "#f5c6cb"}`,
          }}
        >
          {msg.text}
        </Box>
      )}

      {loading && (
        <Box sx={{ py: 8, textAlign: "center", color: C.muted }}>
          <CircularProgress size={22} /> <div style={{ marginTop: 8 }}>Loading theme model…</div>
        </Box>
      )}

      {!loading && (!model || !(model.groups || []).length) && (
        <Box sx={{ py: 6, textAlign: "center", color: C.muted }}>
          <MDTypography variant="h6" sx={{ fontWeight: 700, color: C.ink }}>
            No theme model yet.
          </MDTypography>
          <div style={{ marginTop: 6 }}>{emptyMessage}</div>
        </Box>
      )}

      {!loading && model && (model.groups || []).length > 0 && (
        <>
          {/* Priority filter — default to P1 ("what to watch"); widen to reveal P2/P3/all. Disabled
              while a specific lens is selected (a lens view shows all its themes regardless of band). */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700 }}
            >
              Filter by theme priority:
            </MDTypography>
            {[
              { v: 1, l: "P1" },
              { v: 2, l: "P2" },
              { v: 3, l: "P3" },
              { v: "all", l: "All" },
            ].map((b) => (
              <MDButton
                key={b.v}
                size="small"
                variant={maxBand === b.v ? "contained" : "outlined"}
                color={maxBand === b.v ? "error" : "secondary"}
                onClick={() => {
                  userPickedBand.current = true;
                  setMaxBand(b.v);
                }}
                sx={{ textTransform: "none", minWidth: 0, px: 1.2, py: 0.25 }}
              >
                {b.l}
              </MDButton>
            ))}
          </Box>

          {/* Watchlist filter (themes already on the watchlist, or exclude them) + read state. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700 }}
            >
              Filter by theme on watchlist:
            </MDTypography>
            {[
              { v: "all", l: "All" },
              { v: "has", l: "On watchlist" },
              { v: "exclude", l: "Exclude watchlist" },
            ].map((w) => (
              <MDButton
                key={w.v}
                size="small"
                variant={watchlistSel === w.v ? "contained" : "outlined"}
                color={watchlistSel === w.v ? "info" : "secondary"}
                onClick={() => setWatchlistSel(w.v)}
                sx={{ textTransform: "none", minWidth: 0, px: 1.2, py: 0.25 }}
              >
                {w.l}
              </MDButton>
            ))}
            <span style={{ flex: 1 }} />
            <MDButton
              size="small"
              variant="text"
              color="secondary"
              onClick={markAllRead}
              sx={{ textTransform: "none" }}
            >
              Mark all read
            </MDButton>
          </Box>

          {/* ID185e — risk-category (L2) filter */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <MDTypography
              variant="caption"
              component="span"
              sx={{ color: C.muted, fontWeight: 700 }}
            >
              Filter by risk category:
            </MDTypography>
            <Autocomplete
              multiple
              size="small"
              options={categoryOptions}
              value={catSel}
              onChange={(_, v) => setCatSel(v)}
              disableCloseOnSelect
              limitTags={2}
              sx={{ minWidth: 260, flex: "1 1 260px", maxWidth: 560 }}
              renderInput={(params) => (
                <TextField {...params} placeholder={catSel.length ? "" : "All categories"} />
              )}
            />
          </Box>

          {/* Count of Risk Category boxes shown */}
          <MDTypography
            variant="caption"
            component="div"
            sx={{
              color: C.muted,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              mb: 1.75,
            }}
          >
            {`${nf(shownThemes.length)} risk categor${
              shownThemes.length === 1 ? "y" : "ies"
            } shown · ${nf(allThemesRanked.length)} total`}
          </MDTypography>

          {/* Grid of Risk Category boxes (ID186): each = one L2 theme + its top-3 sub-themes */}
          {shownThemes.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(288px, 1fr))",
                gap: 1.25,
              }}
            >
              {shownThemes.map((x, i) => (
                <CategoryBox
                  key={i}
                  theme={x.t}
                  unread={isUnread(x.t)}
                  onOpen={() => openDetail(x.t)}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 5, textAlign: "center", color: C.muted }}>
              No risk categories match these filters — widen the priority or category filter.
            </Box>
          )}
        </>
      )}

      {/* Drill-down: theme → incidents → news articles */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span style={{ fontWeight: 700, color: C.ink }}>
            {clean(detail?.subTheme || detail?.theme)}
            {detail?.subTheme && (
              <MDTypography
                variant="caption"
                component="span"
                sx={{ fontWeight: 400, color: C.muted, display: "inline" }}
              >
                {" "}
                · in {clean(detail.theme)}
              </MDTypography>
            )}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Advance is done per sub-theme in the list below (PO 2026-09-18). The theme-level button
                only appears for a single-story theme that has no distinct sub-themes to advance. */}
            {detail && !groupIncidentsBySubTheme(detail).some((g) => g.name && g.meta) && (
              <MDButton
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => advanceToWatchlist(detail)}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                ⚑ Advance to Watchlist
              </MDButton>
            )}
            <IconButton size="small" onClick={() => setDetail(null)} aria-label="Close">
              <Icon>close</Icon>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ py: 4, textAlign: "center", color: C.muted }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {/* Lead with the assessment: what's happening + why it matters, under the headline. */}
          {!detailLoading && detail && (detail.summary || detail.whyItMatters) && (
            <Box sx={{ mb: 2.5 }}>
              {(detail.prioritisation?.priorityBand || detail.topDown?.isTopDown) && (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <PriorityBadge p={detail.prioritisation} />
                </div>
              )}
              {detail.summary && (
                <>
                  <MDTypography
                    variant="caption"
                    sx={{
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: C.muted,
                      fontWeight: 700,
                      marginBottom: "3px",
                    }}
                  >
                    What&apos;s happening
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ color: C.ink, lineHeight: 1.6, marginBottom: "12px" }}
                  >
                    {clean(detail.summary)}
                  </MDTypography>
                </>
              )}
              {detail.whyItMatters && (
                <>
                  <MDTypography
                    variant="caption"
                    sx={{
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: C.accent,
                      fontWeight: 700,
                      marginBottom: "3px",
                    }}
                  >
                    Why it matters
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: C.ink, lineHeight: 1.6 }}>
                    {clean(detail.whyItMatters)}
                  </MDTypography>
                </>
              )}
            </Box>
          )}
          {!detailLoading && detail && (detail.incidents || []).length === 0 && (
            <Box sx={{ py: 3, color: C.muted }}>No news found for this theme.</Box>
          )}
          {!detailLoading &&
            groupIncidentsBySubTheme(detail).map((grp, gi) => {
              const collapsible = !!grp.name;
              const open = !collapsible || !!expandedSubs[grp.name];
              return (
                <Box key={gi} sx={{ mb: grp.name ? 2 : 0 }}>
                  {grp.name && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSub(grp.name)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSub(grp.name);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                        borderLeft: `3px solid ${C.accent}`,
                        paddingLeft: 8,
                        paddingTop: 4,
                        paddingBottom: 4,
                        marginBottom: open ? 12 : 0,
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ color: C.accent, fontWeight: 800, width: 10, flex: "none" }}>
                        {open ? "▾" : "▸"}
                      </span>
                      <PriorityBadge
                        p={grp.meta?.prioritisation}
                        dormant={grp.meta ? (grp.meta.recentAlerts || 0) === 0 : false}
                      />
                      <MDTypography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: C.ink, flex: 1, minWidth: 0 }}
                      >
                        {clean(grp.name)}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        component="span"
                        sx={{
                          color: C.muted,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {nf(
                          grp.meta?.alertCount ??
                            grp.incidents.reduce((a, x) => a + (x.count || 0), 0)
                        )}{" "}
                        alerts
                      </MDTypography>
                      {/* Advance THIS sub-theme to the watchlist (not the whole L2 theme). Only real
                          sub-themes (with meta) — never the "More developments" residual group. */}
                      {grp.meta && (
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            advanceToWatchlist(detail, {
                              name: grp.name,
                              prioritisation: grp.meta?.prioritisation,
                            });
                          }}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            flex: "none",
                          }}
                        >
                          ⚑ Advance
                        </MDButton>
                      )}
                    </div>
                  )}
                  {open &&
                    grp.incidents.map((inc, i) => (
                      <Box key={i} sx={{ mb: 2.5, pl: grp.name ? 1.5 : 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 10,
                            marginBottom: 6,
                          }}
                        >
                          <MDTypography variant="h6" sx={{ fontWeight: 700, color: C.ink }}>
                            {pretty(inc.eventTag)}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            component="span"
                            sx={{ color: C.muted, whiteSpace: "nowrap" }}
                          >
                            {nf(inc.count)} {inc.count === 1 ? "article" : "articles"}
                          </MDTypography>
                        </div>
                        {(inc.articles || []).map((a, j) => (
                          <Box
                            key={j}
                            sx={{
                              pl: 1.5,
                              py: 0.75,
                              borderLeft: `2px solid ${C.line}`,
                              mb: 0.75,
                            }}
                          >
                            <a
                              href={a.link || "#"}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: "0.85rem", color: C.accent, fontWeight: 600 }}
                            >
                              {a.title || "(untitled)"}
                            </a>
                            <MDTypography
                              variant="caption"
                              sx={{ color: C.muted, marginTop: "2px" }}
                            >
                              {a.source || ""}
                              {a.ingestedAt
                                ? ` · ${new Date(a.ingestedAt).toLocaleDateString()}`
                                : ""}
                              {a.relevanceScore != null ? ` · relevance ${a.relevanceScore}` : ""}
                            </MDTypography>
                            {a.attachmentBlob && (
                              <button
                                type="button"
                                onClick={() => openAttachment(a.attachmentBlob)}
                                style={{
                                  border: "none",
                                  background: "none",
                                  padding: 0,
                                  marginTop: 3,
                                  cursor: "pointer",
                                  color: C.accent,
                                  fontSize: "0.74rem",
                                  fontWeight: 600,
                                  textDecoration: "underline",
                                }}
                              >
                                📎 View source document
                                {a.attachmentName ? ` (${a.attachmentName})` : ""}
                              </button>
                            )}
                            {a.whyItMatters && (
                              <MDTypography
                                variant="caption"
                                sx={{ color: C.ink, marginTop: "3px" }}
                              >
                                {a.whyItMatters}
                              </MDTypography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    ))}
                </Box>
              );
            })}
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}

export default AnticipateHorizonScanV2;
